import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import {
  Row,
  Col,
  Button,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  Popconfirm,
  Typography,
  message,
  Space,
  Tag,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  DownloadOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import { constants } from '../../constants'
import {
  getPatientNotes,
  getPatientNotesCategories,
  addPatientNote,
  updatePatientNote,
  deletePatientNote,
  getPatientNotesCount,
} from '../../store/action/patientnotes.action'

const { TextArea } = Input
const { Option } = Select
const { Text } = Typography

const PatientNotes = ({ patient, ...props }) => {
  const [form] = Form.useForm()
  const [modalVisible, setModalVisible] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    showSizeChanger: true,
    showQuickJumper: true,
  })


  useEffect(() => {
    if (patient?._id) {
      loadPatientNotes()
      loadCategories()
    }
  }, [patient?._id, pagination.current, pagination.pageSize])


  const loadPatientNotes = async () => {
    if (!patient?._id) {
      return
    }
    
    const input = {
      patient_id: patient._id,
      metadata: {
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    }
    try {
      const response = await props.getPatientNotes(input)
      return response
    } catch (error) {
      throw error
    }
  }

  const loadCategories = async () => {
    try {
      await props.getPatientNotesCategories()
    } catch (error) {
      console.error('Error loading categories, using fallback:', error)
      // Don't break the component if categories fail to load
    }
  }

  const handleAddNote = () => {
    setEditingNote(null)
    setSelectedCategory(null)
    form.resetFields()
    const defaultCategory = props.categories?.find((cat) => cat.is_default)
    form.setFieldsValue({
      datetime: moment(),
      category: defaultCategory?.category_name || 'General',
    })
    if (defaultCategory) {
      setSelectedCategory(defaultCategory)
    }
    setModalVisible(true)
  }

  const handleEditNote = (note) => {
    setEditingNote(note)
    const category = props.categories?.find((cat) => cat.category_name === note.category)
    setSelectedCategory(category || null)
    form.setFieldsValue({
      notes: note.notes,
      category: note.category,
      time_logged: formatTimeForInput(note.time_logged),
      datetime: moment(note.datetime),
    })
    setModalVisible(true)
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    setEditingNote(null)
    setSelectedCategory(null)
    form.resetFields()
  }

  const handleCategoryChange = (categoryName) => {
    const category = props.categories?.find((cat) => cat.category_name === categoryName)
    setSelectedCategory(category || null)
  }

  const handleSubmit = async (values) => {
    try {
      const noteData = {
        ...values,
        patient_id: patient._id,
        datetime: values.datetime.toISOString(),
        time_logged: parseTimeInput(values.time_logged),
      }

      if (editingNote) {
        noteData._id = editingNote._id
        const response = await props.updatePatientNote(noteData)
        if (response.status === constants.MESSAGE.SUCCESS) {
          message.success('Note updated successfully')
          handleModalCancel() // Use the cancel handler to properly reset state
          await loadPatientNotes() // Reload current page
        } else {
          message.error(response.message || 'Failed to update note')
        }
      } else {
        const response = await props.addPatientNote(noteData)
        if (response.status === constants.MESSAGE.SUCCESS) {
          message.success('Note added successfully')
          handleModalCancel() // Use the cancel handler to properly reset state
          // Reset pagination to first page to see the new note
          setPagination(prev => ({ ...prev, current: 1 }))
          // The useEffect will trigger loadPatientNotes when pagination changes
        } else {
          message.error(response.message || 'Failed to add note')
        }
      }
    } catch (error) {
      console.error('Error saving note:', error)
      message.error('An error occurred while saving the note')
    }
  }

  const handleDeleteNote = async (noteId) => {
    try {
      const response = await props.deletePatientNote(noteId)
      if (response.status === constants.MESSAGE.SUCCESS) {
        message.success('Note deleted successfully')
        await loadPatientNotes() // Reload current page
      } else {
        message.error(response.message || 'Failed to delete note')
      }
    } catch (error) {
      console.error('Error deleting note:', error)
      message.error('An error occurred while deleting the note')
    }
  }

  const handleCopyNote = async (noteText) => {
    try {
      await navigator.clipboard.writeText(noteText)
      message.success('Note copied')
    } catch (error) {
      console.error('Error copying note:', error)
      message.error('Failed to copy note')
    }
  }

  const handleDownloadCSV = async () => {
    try {
      // Get all notes (not just current page)
      const allNotesInput = {
        patient_id: patient._id,
        metadata: {
          current: 1,
          pageSize: 1000, // Get a large number to capture all notes
        },
      }
      
      const response = await props.getPatientNotes(allNotesInput)
      
      if (response.status === constants.MESSAGE.SUCCESS && response.notes) {
        // Create CSV content
        const csvHeaders = ['"Date & Time"', '"Category"', '"Note"', '"Entered by"']
        const csvRows = response.notes.map(note => [
          `"${moment(note.datetime).format('MM/DD/YYYY HH:mm')}"`,
          `"${note.category || ''}"`,
          `"${(note.notes || '').replace(/"/g, '""').replace(/\r?\n/g, '\n')}"`, // Escape quotes, preserve line breaks as actual LF
          `"${`${note.created_by?.firstname || ''} ${note.created_by?.lastname || ''}`.trim()}"`
        ])
        
        // Combine headers and rows
        const csvContent = [csvHeaders, ...csvRows]
          .map(row => row.join(','))
          .join('\n')
        
        // Add UTF-8 BOM for proper encoding recognition
        const utf8BOM = '\uFEFF'
        const csvWithBOM = utf8BOM + csvContent
        
        // Create and download file with explicit UTF-8 encoding
        const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' })
        const link = document.createElement('a')
        const url = URL.createObjectURL(blob)
        
        link.setAttribute('href', url)
        link.setAttribute('download', `patient-notes-${patient.firstname}-${patient.lastname}-${moment().format('YYYY-MM-DD')}.csv`)
        link.style.visibility = 'hidden'
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        message.success('Notes exported to CSV successfully')
      } else {
        message.error('Failed to export notes')
      }
    } catch (error) {
      console.error('Error downloading CSV:', error)
      message.error('An error occurred while exporting notes')
    }
  }

  const parseTimeInput = (timeStr) => {
    if (!timeStr) return undefined
    
    const timeString = timeStr.toString().toLowerCase().trim()
    let totalMinutes = 0
    
    // Parse patterns like "1h 30m", "2h", "45m", "1d 2h 30m"
    const dayMatch = timeString.match(/(\d+)d/)
    const hourMatch = timeString.match(/(\d+)h/)
    const minuteMatch = timeString.match(/(\d+)m/)
    
    if (dayMatch) totalMinutes += parseInt(dayMatch[1]) * 24 * 60
    if (hourMatch) totalMinutes += parseInt(hourMatch[1]) * 60
    if (minuteMatch) totalMinutes += parseInt(minuteMatch[1])
    
    // If no units specified, assume minutes
    if (!dayMatch && !hourMatch && !minuteMatch && !isNaN(timeString)) {
      totalMinutes = parseInt(timeString)
    }
    
    return totalMinutes || undefined
  }

  const formatTimeForInput = (minutes) => {
    if (!minutes) return ''
    
    const days = Math.floor(minutes / (24 * 60))
    const remainingMinutes = minutes % (24 * 60)
    const hours = Math.floor(remainingMinutes / 60)
    const mins = remainingMinutes % 60
    
    let result = ''
    if (days > 0) result += `${days}d `
    if (hours > 0) result += `${hours}h `
    if (mins > 0) result += `${mins}m`
    
    return result.trim()
  }

  const formatTimeDisplay = (minutes) => {
    if (!minutes) return ''
    
    const days = Math.floor(minutes / (24 * 60))
    const remainingMinutes = minutes % (24 * 60)
    const hours = Math.floor(remainingMinutes / 60)
    const mins = remainingMinutes % 60
    
    let parts = []
    if (days > 0) parts.push(`${days}d`)
    if (hours > 0) parts.push(`${hours}h`)
    if (mins > 0) parts.push(`${mins}m`)
    
    return parts.join(' ')
  }

  const columns = [
    {
      title: 'Date & Time',
      dataIndex: 'datetime',
      key: 'datetime',
      width: 140,
      render: (datetime) => moment(datetime).format('MM/DD/YYYY HH:mm'),
      sorter: (a, b) => moment(a.datetime).unix() - moment(b.datetime).unix(),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 400,
      render: (notes) => {
        const lines = notes?.split('\n') || []
        const displayLines = lines.slice(0, 10)
        const hasMoreLines = lines.length > 10
        
        return (
          <div style={{ maxWidth: 400, whiteSpace: 'pre-wrap' }}>
            {displayLines.join('\n')}
            {hasMoreLines && (
              <Text type="secondary" style={{ fontStyle: 'italic' }}>
                <br />... ({lines.length - 10} more lines)
              </Text>
            )}
          </div>
        )
      },
    },
    {
      title: 'Time Logged',
      dataIndex: 'time_logged',
      key: 'time_logged',
      width: 100,
      render: (time_logged) => (
        <Space>
          {time_logged && <ClockCircleOutlined />}
          {formatTimeDisplay(time_logged) || '-'}
        </Space>
      ),
    },
    {
      title: 'Created By',
      dataIndex: 'created_by',
      key: 'created_by',
      width: 120,
      render: (created_by) => `${created_by?.firstname} ${created_by?.lastname}`,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<CopyOutlined />}
            onClick={() => handleCopyNote(record.notes)}
            title="Copy note"
          />
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditNote(record)}
            title="Edit note"
          />
          <Popconfirm
            title="Are you sure you want to delete this note?"
            onConfirm={() => handleDeleteNote(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              title="Delete note"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const handleTableChange = (paginationConfig) => {
    setPagination({
      ...pagination,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize,
    })
  }


  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Text strong style={{ fontSize: '16px' }}>
            Patient Notes ({props.total || 0})
          </Text>
        </Col>
        <Col>
          <Space>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleDownloadCSV}
              disabled={!patient?._id || !props.notes?.length}
              title="Download all notes as CSV"
              style={{ borderRadius: '5px' }}
            >
              Export CSV
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddNote}
              disabled={!patient?._id}
              style={{ backgroundColor: '#000000', borderColor: '#000000', borderRadius: '5px' }}
            >
              Add Note
            </Button>
          </Space>
        </Col>
      </Row>


      <Table
        columns={columns}
        dataSource={props.notes}
        rowKey="_id"
        loading={props.isLoading}
        pagination={{
          ...pagination,
          total: props.total,
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} notes`,
        }}
        onChange={handleTableChange}
        size="small"
      />

      <Modal
        title={editingNote ? 'Edit Patient Note' : 'Add Patient Note'}
        visible={modalVisible}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            datetime: moment(),
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="category"
                label="Category"
                rules={[{ required: true, message: 'Please select a category' }]}
              >
                <Select 
                  placeholder="Select category"
                  onChange={handleCategoryChange}
                >
                  {props.categories?.map((category) => (
                    <Option key={category._id} value={category.category_name}>
                      {category.category_name}
                      {category.is_default && ' (Default)'}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="datetime"
                label="Date & Time"
                rules={[{ required: true, message: 'Please select date and time' }]}
              >
                <DatePicker
                  showTime
                  format="MM/DD/YYYY HH:mm"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          {selectedCategory?.category_description && (
            <div style={{ 
              marginTop: '-8px',
              marginBottom: '16px'
            }}>
              <Text style={{ fontSize: '12px', fontWeight: '600', color: '#666' }}>
                Category Description:
              </Text>
              <br />
              <Text style={{ fontSize: '13px', lineHeight: '1.4', color: '#555' }}>
                {selectedCategory.category_description}
              </Text>
            </div>
          )}

          <Form.Item
            name="notes"
            label="Notes"
            rules={[{ required: true, message: 'Please enter notes' }]}
          >
            <TextArea
              rows={4}
              placeholder="Enter notes about the patient interaction..."
            />
          </Form.Item>

          <Form.Item
            name="time_logged"
            label="Time Logged (optional)"
            help="Examples: 30m, 1h 30m, 2h, 1d 2h 30m"
          >
            <Input
              placeholder="e.g., 30m or 1h 30m"
              suffix={<ClockCircleOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={handleModalCancel}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={props.isLoading}>
                {editingNote ? 'Update' : 'Add'} Note
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

PatientNotes.propTypes = {
  patient: PropTypes.object,
  notes: PropTypes.array,
  categories: PropTypes.array,
  total: PropTypes.number,
  isLoading: PropTypes.bool,
  getPatientNotes: PropTypes.func.isRequired,
  getPatientNotesCategories: PropTypes.func.isRequired,
  addPatientNote: PropTypes.func.isRequired,
  updatePatientNote: PropTypes.func.isRequired,
  deletePatientNote: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => ({
  notes: state.patientnotes.notes,
  categories: state.patientnotes.categories,
  total: state.patientnotes.total,
  isLoading: state.patientnotes.isLoading,
})

const mapDispatchToProps = (dispatch) => ({
  getPatientNotes: (input) => dispatch(getPatientNotes(input)),
  getPatientNotesCategories: () => dispatch(getPatientNotesCategories()),
  addPatientNote: (input) => dispatch(addPatientNote(input)),
  updatePatientNote: (input) => dispatch(updatePatientNote(input)),
  deletePatientNote: (id) => dispatch(deletePatientNote(id)),
  getPatientNotesCount: (patientId) => dispatch(getPatientNotesCount(patientId)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PatientNotes)